import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AppContext } from '../App'
import { useIsMobile } from '../hooks/useIsMobile'
import { useGet } from '../hooks/useGet'
import { getShortText, formatDateTime } from '../common/utilities'
import { RadAppLayout } from '../common/RadAppLayout'
import { RadBox } from '../common/RadBox'
import { RadButton } from '../common/RadButton'
import { RadCards } from '../common/RadCards'
import { RadExternalLink } from '../common/RadExternalLink'
import { RadGrid } from '../common/RadGrid'
import { RadHeader } from '../common/RadHeader'
import { RadInput2 } from '../common/RadInput2'
import { RadLink } from '../common/RadLink'
import { RadMultiselect } from '../common/RadMultiselect'
import { RadPagination } from '../common/RadPagination'
import { RadPopover } from '../common/RadPopover'
import { RadSelect } from '../common/RadSelect'
import { EntityModal } from '../shared/EntityModal'
import { RadSpaceBetween } from '../common/RadSpaceBetween'
import { SortPopover } from '../shared/SortPopover'
import { FancyListHeader } from '../shared/FancyListHeader'
import { RadContainer } from '../common/RadContainer'
import { RadBadge } from '../common/RadBadge'
import './ResourceList.scss'
import { BottomPagination } from '../shared/BottomPagination'
import { exportToCsv } from '../common/exportToCsv'

export function ResourceList () {
  const pageLength = 20
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { loadingCount, user } = useContext(AppContext)
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedEntity, setSelectedEntity] = useState(null)
  const [targetPopulationFilteringText, setTargetPopulationFilteringText] = useState('')
  const currentPageIndex = searchParams.get('page') != null ? parseInt(searchParams.get('page')) : 1
  const searchText = searchParams.get('search') ?? ''
  const sectorId = searchParams.get('sector') ?? ''
  const tagIds = searchParams.get('tags') ?? ''
  const resourceTypeId = searchParams.get('resource-type') ?? ''
  const priorityPopulationIds = searchParams.get('priority-populations') ?? ''
  const dashFundingProgramId = searchParams.get('dash-funding-program') ?? ''
  const collectionId = searchParams.get('collectionId') ?? ''
  const sortOrder = searchParams.get('sort') ?? 'alphabetical'

  const { data: resourcesData, count } = useGet([
    '/api/resource',
    `?search=${searchText}`,
    `&sectorId=${sectorId}`,
    `&tagIds=${tagIds ?? ''}`,
    `&resourceTypeId=${resourceTypeId}`,
    `&priorityPopulationIds=${priorityPopulationIds}`,
    `&dashFundingProgramId=${dashFundingProgramId}`,
    `&collectionId=${collectionId}`,
    `&limit=${pageLength}` +
    `&offset=${(currentPageIndex - 1) * pageLength}`,
    `&sortOrder=${sortOrder}`
  ].join(''))
  const { data: resourceTypeOptions } = useGet('/api/option/type?entity=resource&all=true')
  const { data: sectorOptions } = useGet('/api/option/sector?all=true')
  const { data: tags } = useGet('/api/tag?limit=5000')
  const { data: targetPopulationOptions } = useGet(`/api/option/priority-population?search=${targetPopulationFilteringText}&limit=500`)
  const { data: typeOptions } = useGet('/api/option/type?entity=resource')
  const { data: collectionOptions } = useGet('/api/option/collection?all=true')
  const { data: userInfo } = useGet('/api/user/current')

  useEffect(() => {
    if (searchParams.get('resourceId') != null) {
      setSelectedEntity({ type: 'resource', id: parseInt(searchParams.get('resourceId')) })
      searchParams.delete('resourceId')
      setSearchParams(searchParams)
    }
  }, [searchParams])

  if (
    resourcesData != null &&
    resourceTypeOptions != null &&
    sectorOptions != null &&
    tags != null &&
    targetPopulationOptions != null &&
    typeOptions != null &&
    collectionOptions != null &&
    userInfo != null
  ) {
    const tagOptions = tags.map((x) => { return { value: x.id.toString(), label: x.name } })
    const canCreate = userInfo.roles.some((x) => x.name === 'Admin') ||
      userInfo.roles.some((x) => x.permissions.some((y) => y.name === 'Resource Create'))
    const publicUser = userInfo.roles.some((x) => x.name === 'Public User') && userInfo.roles.length === 1
    const { resources, collection } = resourcesData

    async function exportResources () {
      await exportToCsv({
        uri: [
          '/api/resource',
          `?search=${searchText}`,
          `&sectorId=${sectorId}`,
          `&tagIds=${tagIds ?? ''}`,
          `&resourceTypeId=${resourceTypeId}`,
          `&priorityPopulationIds=${priorityPopulationIds}`,
          `&dashFundingProgramId=${dashFundingProgramId}`,
          `&collectionId=${collectionId}`,
          `&sortOrder=${sortOrder}`,
          '&export=true'
        ].join(''),
        columnNames: [
          'Title',
          'Type',
          'Source',
          'Link',
          'File Name',
          'Text',
          'Description',
          'Publisher',
          'Publication Date',
          'Author',
          'Sectors',
          'Framework Tags',
          'Projects',
          'Priority Populations',
          'Project Types',
          'Topics',
          'Collections',
          'Is Private',
          'Shares'
        ],
        mapper: (items) => items.map(x => ([
          x.title,
          x.type,
          x.sourceId === 0 ? 'File' : x.sourceId === 1 ? 'Link' : x.sourceId === 2 ? 'Text' : null,
          x.link,
          x.file?.replace(/^\/api\/storage\/resource\/[^/]+\//, ''),
          x.text,
          x.description,
          x.publisher,
          x.publicationDate,
          x.author,
          x.sectors,
          x.frameworkTags,
          x.projects,
          x.priorityPopulations,
          x.projectTypes,
          x.topics,
          x.collections,
          x.isPrivate ? 'Yes' : 'No',
          x.shares
        ])),
        filenamePrefix: 'resources',
        user,
        onError: (e) => console.error(e)
      })
    }

    return (
      <RadAppLayout
        contentHeader={
          <FancyListHeader
            title='Resources'
            description={
              <RadBox>
                We continually curate resources into collections. You can learn more on our dedicated <RadLink href='/collection'>Collections page</RadLink>. You can also use the 'collections' filter below to view the resources contained within each collection.
              </RadBox>
            }
            count={count}
            actions={
              <RadSpaceBetween direction='horizontal' size='xs'>
                {!publicUser && <RadButton disabled={!canCreate} onClick={() => navigate('/resource/create')}>Create</RadButton>}
                <RadButton onClick={exportResources}>Export</RadButton>
              </RadSpaceBetween>
            }
          />
        }
        content={
          <RadSpaceBetween size='l'>
            <RadGrid
              gridDefinition={[
                { colspan: { default: 12, xs: 8 } },
                { colspan: { default: 12, xs: 4 } }
              ]}
            >
              <RadSpaceBetween direction='horizontal' size='xs'>
                <SortPopover
                  sortOrder={sortOrder}
                  loadingCount={loadingCount}
                />
                <FilterPopover
                  resourceTypeId={resourceTypeId}
                  sectorId={sectorId}
                  tagIds={tagIds}
                  priorityPopulationIds={priorityPopulationIds}
                  collectionId={collectionId}
                  targetPopulationFilteringText={targetPopulationFilteringText}
                  setTargetPopulationFilteringText={setTargetPopulationFilteringText}
                  resourceTypeOptions={resourceTypeOptions}
                  sectorOptions={sectorOptions}
                  tagOptions={tagOptions}
                  targetPopulationOptions={targetPopulationOptions}
                  collectionOptions={collectionOptions}
                  loadingCount={loadingCount}
                />
                <RadBox className='rad-search-input'>
                  <RadInput2
                    initialValue={searchText}
                    placeholder='Search resources to sort by relevance'
                    type='search'
                    onEnter={({ detail }) => {
                      if (detail.value !== '') {
                        searchParams.set('search', detail.value)
                      } else {
                        searchParams.delete('search')
                      }
                      if (detail.value === '') {
                        searchParams.delete('sort')
                      } else {
                        searchParams.set('sort', 'relevance')
                      }
                      searchParams.delete('page')
                      setSearchParams(searchParams)
                    }}
                  />
                </RadBox>
              </RadSpaceBetween>
              <RadBox float={isMobile ? 'none' : 'right'}>
                <RadPagination
                  currentPageIndex={currentPageIndex}
                  pagesCount={Math.ceil(count / pageLength)}
                  onChange={({ detail }) => {
                    searchParams.set('page', detail.currentPageIndex)
                    setSearchParams(searchParams)
                  }}
                  ariaLabels={{
                    nextPageLabel: 'Next page',
                    previousPageLabel: 'Previous page',
                    pageLabel: pageNumber => `Page ${pageNumber} of all pages`
                  }}
                />
              </RadBox>
            </RadGrid>
            <RadSpaceBetween size='l'>
              {
                (sectorId !== '' || tagIds !== '' || priorityPopulationIds !== '' || resourceTypeId !== '') &&
                  <RadBox color='text-status-inactive'>
                    {'Filter(s): ' +
                    [
                      sectorId !== '' ? `Sector: ${sectorOptions.find(x => x.value === sectorId)?.label}` : null,
                      tagIds !== '' ? `Framework Tags: ${tagOptions.filter(x => tagIds.split('-').includes(x.value)).map(x => x.label).join(', ')}` : null,
                      priorityPopulationIds !== ''
                        ? 'Priority Populations: ' + targetPopulationOptions
                          .filter(x => priorityPopulationIds.split('-').includes(x.value)).map(x => x.label).join(', ')
                        : null,
                      resourceTypeId !== '' ? 'Type: ' + resourceTypeOptions.find(x => x.value === resourceTypeId)?.label : null
                    ]
                      .filter(x => x !== null)
                      .join(', ')}
                  </RadBox>
              }
              {
                collection != null &&
                  <RadContainer className='collection-overview'>
                    <RadSpaceBetween size='l'>
                      <RadSpaceBetween direction='horizontal' size='s' alignItems='center'>
                        <RadHeader variant='h2'><h3 className='collection-header'>{collection.name}</h3></RadHeader>
                        <RadBadge collection>Collection</RadBadge>
                      </RadSpaceBetween>
                      <RadGrid
                        gridDefinition={[
                          { colspan: { default: 12, xs: 4 } },
                          { colspan: { default: 12, xs: 8 } }
                        ]}
                        gap='s'
                      >
                        <RadBox className='collection-subtitle'>{collection.subtitle}</RadBox>
                        <RadBox>{collection.description}</RadBox>
                      </RadGrid>
                      <RadGrid
                        gridDefinition={[
                          { colspan: { default: 12, xs: 6 } },
                          { colspan: { default: 12, xs: 6 } }
                        ]}
                        gap='s'
                      >
                        <RadBox color='text-body-secondary' fontSize='body-s' fontWeight='normal'>
                          {`Updated: ${formatDateTime(collection.updatedAt)}`}
                        </RadBox>
                        <RadBox float={isMobile ? 'none' : 'right'}>
                          <RadSpaceBetween direction='horizontal' size='s'>
                            {
                            collection.canEdit &&
                              <RadButton href={`/collection/${collection.id}/edit`}>
                                Edit Collection
                              </RadButton>
                          }
                            {
                            collection.featuredBlogPostUrl &&
                              <RadButton
                                href={collection.featuredBlogPostUrl}
                                target='_blank'
                                external
                              >
                                About Collection
                              </RadButton>
                          }
                          </RadSpaceBetween>
                        </RadBox>
                      </RadGrid>
                    </RadSpaceBetween>
                  </RadContainer>
              }
            </RadSpaceBetween>
            <RadCards
              className='resource-cards'
              cardDefinition={{
                header: item => {
                  if (isMobile) {
                    return (
                      <RadLink className='font-size-24' fontSize='heading-s' href={`resource/${item.id}`}>
                        {item.title}
                      </RadLink>
                    )
                  } else {
                    return (
                      <RadButton
                        onClick={() => setSelectedEntity({ type: 'resource', id: item.id })}
                        className='font-size-24'
                        variant='inline-link'
                      >
                        {item.title}
                      </RadButton>
                    )
                  }
                },
                sections: [
                  {
                    id: 'source',
                    content: item => {
                      switch (item.sourceId) {
                        case 0:
                          return <RadExternalLink key={1} href={item.file} />
                        case 1:
                          return <RadLink className='font-size-16' external externalIconAriaLabel='Opens in a new tab' href={item.link} target='_blank'>link</RadLink>
                        case 2:
                          return 'Text'
                        default:
                          return '-'
                      }
                    }
                  },
                  {
                    id: 'type',
                    header: 'Type',
                    content: item => item.type ?? '-'
                  },
                  {
                    id: 'summary',
                    content: item => getShortText(item.summary) ?? '-'
                  },
                  {
                    id: 'sectors',
                    content: item => <RadBox fontWeight='bold'>{item.sectors?.join(', ')}</RadBox>
                  },
                  {
                    id: 'updatedAt',
                    content: item => <RadBox color='text-status-inactive'>{`Updated: ${formatDateTime(item.updatedAt)}`}</RadBox>
                  }
                ]
              }}
              cardsPerRow={[
                { cards: 1 },
                { minWidth: 640, cards: 2 },
                { minWidth: 960, cards: 3 },
                { minWidth: 1280, cards: 4 }
              ]}
              items={resources}
              variant='full-page'
              empty={
                <RadBox textAlign='center' color='inherit'>
                  No records meet this search criteria.
                </RadBox>
              }
            />
            <BottomPagination
              currentPageIndex={currentPageIndex}
              count={count}
              pageLength={pageLength}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
            />
            <EntityModal
              selectedEntity={selectedEntity}
              setSelectedEntity={setSelectedEntity}
              closeModal={() => setSelectedEntity(null)}
            />
          </RadSpaceBetween>
        }
      />
    )
  }
}

function FilterPopover ({
  dashFundingProgramOptions,
  loadingCount,
  organizationTypeOptions,
  priorityPopulationIds,
  projectTypeOptions,
  resourceTypeId,
  resourceTypeOptions,
  collectionId,
  collectionOptions,
  sectorId,
  sectorOptions,
  setTargetPopulationFilteringText,
  tagIds,
  tagOptions,
  targetPopulationFilteringText,
  targetPopulationOptions
}) {
  const [searchParams, setSearchParams] = useSearchParams()

  return (
    <RadPopover
      position='bottom'
      size='large'
      triggerType='custom'
      content={
        <RadSpaceBetween size='l'>
          <RadHeader
            variant='h2'
          >
            Filter
          </RadHeader>
          <RadSpaceBetween size='xs'>
            <RadSelect
              disabled={loadingCount > 0}
              selectedOption={collectionOptions.find(x => x.value === collectionId) ?? collectionOptions[0]}
              onChange={({ detail }) => {
                if (detail.selectedOption.value === '') {
                  searchParams.delete('collectionId')
                } else {
                  searchParams.set('collectionId', detail.selectedOption.value)
                }
                searchParams.delete('page')
                setSearchParams(searchParams)
              }}
              options={collectionOptions}
              selectedAriaLabel='Selected'
              empty='No matches found'
            />
            <RadSelect
              disabled={loadingCount > 0}
              filteringType='auto'
              selectedOption={sectorOptions.find(x => x.value === sectorId) ?? sectorOptions[0]}
              onChange={({ detail }) => {
                if (detail.selectedOption.value === '') {
                  searchParams.delete('sector')
                } else {
                  searchParams.set('sector', detail.selectedOption.value)
                }
                searchParams.delete('page')
                setSearchParams(searchParams)
              }}
              options={sectorOptions}
              enteredTextLabel={value => value}
              selectedAriaLabel='Selected'
              placeholder='Choose a record type'
              empty='No matches found'
            />
            <RadMultiselect
              disabled={loadingCount > 0}
              filteringType='auto'
              selectedOptions={tagOptions.filter((x) => tagIds.split('-').includes(x.value))}
              onChange={({ detail }) => {
                const ids = detail.selectedOptions.map((x) => x.value).join('-')
                if (ids === '') {
                  searchParams.delete('tags')
                } else {
                  searchParams.set('tags', ids)
                }
                searchParams.delete('page')
                setSearchParams(searchParams)
              }}
              options={tagOptions}
              selectedAriaLabel='Selected'
              deselectAriaLabel={e => `Remove ${e.label}`}
              placeholder='Choose framework tags'
              keepOpen={false}
            />
            <RadMultiselect
              disabled={loadingCount > 0}
              filteringType='manual'
              selectedOptions={targetPopulationOptions.filter((x) => priorityPopulationIds.split('-').includes(x.value))}
              onChange={({ detail }) => {
                const ids = detail.selectedOptions.map((x) => x.value).join('-')
                if (ids === '') {
                  searchParams.delete('priority-populations')
                } else {
                  searchParams.set('priority-populations', ids)
                }
                searchParams.delete('page')
                setSearchParams(searchParams)
              }}
              onLoadItems={({ detail }) => {
                setTargetPopulationFilteringText(detail.filteringText)
              }}
              options={targetPopulationOptions}
              selectedAriaLabel='Selected'
              deselectAriaLabel={e => `Remove ${e.label}`}
              placeholder='Choose priority populations'
              keepOpen={false}
            />
            <RadSelect
              disabled={loadingCount > 0}
              selectedOption={resourceTypeOptions.find(x => x.value === resourceTypeId) ?? resourceTypeOptions[0]}
              onChange={({ detail }) => {
                if (detail.selectedOption.value === '') {
                  searchParams.delete('resource-type')
                } else {
                  searchParams.set('resource-type', detail.selectedOption.value)
                }
                searchParams.delete('page')
                setSearchParams(searchParams)
              }}
              options={resourceTypeOptions}
              selectedAriaLabel='Selected'
              empty='No matches found'
            />
          </RadSpaceBetween>
          <RadBox float='right'>
            <RadButton
              onClick={() => {
                searchParams.delete('sector')
                searchParams.delete('tags')
                searchParams.delete('record-type')
                searchParams.delete('resource-type')
                searchParams.delete('organization-type')
                searchParams.delete('project-type')
                searchParams.delete('priority-populations')
                searchParams.delete('dash-funding-program')
                searchParams.delete('page')
                searchParams.delete('collectionId')
                setSearchParams(searchParams)
              }}
            >
              Clear All Filters
            </RadButton>
          </RadBox>
        </RadSpaceBetween>
      }
    >
      <RadButton
        className='secondary'
        iconAlign='right'
        iconName='angle-down'
      >
        Filter
      </RadButton>
    </RadPopover>
  )
}
